import { memo } from 'react';

import { Box, rem } from '@bonusly/design-system';

import { useBizyFlightAnchor } from '../bizy';
import { BizySidePanel } from './bizy-side-panel';

const EASING = 'cubic-bezier(.4,0,.2,1)';

export interface BizySidePanelDockProps {
	isOpen: boolean;
	isFullscreen: boolean;
	isResizing: boolean;
	panelWidth: number;
	// Nav sidebar's current width in px — used to size the fullscreen overlay
	// against the remaining viewport (the dock's own position comes from flex
	// order, not a computed `left`).
	sidebarPx: number;
	top: string;
	height: string;
}

/**
 * The Bizy panel's own column in AppLayout's flex row — a real flex sibling
 * of the sidebar and main content, so widths stay in sync automatically.
 * Open/close animates a single, real `flexBasis`.
 *
 * Fullscreen does NOT grow the flex reservation — it stays capped at the
 * docked `panelWidth`, so main content never resizes on fullscreen toggle.
 * The content instead overlays via `position: fixed` (escaping the
 * reservation box's `overflow: hidden`) only while open AND fullscreen;
 * every other combination renders as a normal, pinned-width child so its
 * layout is computed once, not every animation frame.
 */
export const BizySidePanelDock = memo(function BizySidePanelDock({
	isOpen,
	isFullscreen,
	isResizing,
	panelWidth,
	sidebarPx,
	top,
	height,
}: BizySidePanelDockProps) {
	const bizyPanelFallbackRef = useBizyFlightAnchor('navFallback');

	const dockedWidth = rem(panelWidth);
	// Reservation is ALWAYS the docked width (or 0 closed) — never the
	// fullscreen width. Main only ever shrinks by this much, fullscreen or not.
	const flexBasis = isOpen ? dockedWidth : '0px';
	const isOverlaying = isOpen && isFullscreen;

	return (
		<Box
			position="sticky"
			top={top}
			height={height}
			flexGrow={0}
			flexShrink={0}
			flexBasis={flexBasis}
			// Without this, flex's `min-width: auto` default floors the closed
			// (flex-basis: 0) panel at its content's width, not 0.
			minW="0"
			overflow="hidden"
			zIndex="docked"
			transition={isResizing ? 'none' : `flex-basis 0.3s ${EASING}`}
		>
			<Box
				position={isOverlaying ? 'fixed' : 'relative'}
				top={isOverlaying ? top : 0}
				left={isOverlaying ? rem(sidebarPx) : 0}
				width={isOverlaying ? `calc(100vw - ${rem(sidebarPx)})` : dockedWidth}
				height={isOverlaying ? height : '100%'}
				overflow="hidden"
				borderRight={isOpen && !isFullscreen ? '1px solid' : 'none'}
				borderColor="border.soft"
				zIndex={isOverlaying ? 'overlay' : 'docked'}
			>
				<Box ref={bizyPanelFallbackRef} position="absolute" top={rem(80)} left="calc(50% - 40px)" boxSize={rem(80)} pointerEvents="none" />
				<BizySidePanel gradientTheme="cool-subtle" panelLeftEdge={sidebarPx} />
			</Box>
		</Box>
	);
});
